// src/pages/admin/InvenireCandidatesPage.jsx
// Super Admin Candidate Upload - standardized header with logo

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackPageView, trackCandidateInvited } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.invenire.app";

export default function InvenireCandidatesPage() {
  const navigate = useNavigate();
  
  // Client/tenant state
  const [clients, setClients] = useState([]);
  const [selectedClientKey, setSelectedClientKey] = useState("");
  const [loadingClients, setLoadingClients] = useState(true);
  
  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Form state
  const [selectedJobId, setSelectedJobId] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Result state
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  const [blueprintReady, setBlueprintReady] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = 'Invenire - Candidate Upload';
  }, []);

  // Track page view
  useEffect(() => {
    trackPageView('/admin/candidates', 'Invenire Candidates');
  }, []);
  
  // Load clients on mount
  useEffect(() => {
    async function loadClients() {
      try {
        const response = await fetch(`${API_BASE}/tenants`);
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (data.tenants || []);
          setClients(list);
          
          // Auto-select first client if available
          if (list.length > 0) {
            setSelectedClientKey(list[0].key);
          }
        }
      } catch (err) {
        console.error("Failed to load clients:", err);
      } finally {
        setLoadingClients(false);
      }
    }
    loadClients();
  }, []);
  
  // Load jobs when client changes
  useEffect(() => {
    if (!selectedClientKey) {
      setJobs([]);
      setSelectedJobId("");
      return;
    }
    
    async function loadJobs() {
      setLoadingJobs(true);
      try {
        const response = await fetch(`${API_BASE}/jobs`, {
          headers: { "X-Tenant-Key": selectedClientKey }
        });
        if (response.ok) {
          const data = await response.json();
          const jobList = Array.isArray(data) ? data : (data.jobs || []);
          setJobs(jobList);
          
          // Auto-select first job if available
          if (jobList.length > 0) {
            setSelectedJobId(jobList[0].id.toString());
          } else {
            setSelectedJobId("");
          }
        }
      } catch (err) {
        console.error("Failed to load jobs:", err);
      } finally {
        setLoadingJobs(false);
      }
    }
    loadJobs();
  }, [selectedClientKey]);
  
  // Poll for blueprint status
  useEffect(() => {
    if (!result || blueprintReady) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/profiles/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ interview_id: result.interview_id })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.blueprint_ready) {
            setBlueprintReady(true);
            setTimeout(() => setEmailSent(true), 1500);
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error("Blueprint poll error:", err);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [result, blueprintReady]);
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      if (!validTypes.includes(file.type) && !file.name.endsWith(".pdf") && !file.name.endsWith(".docx")) {
        setResultError("Please upload a PDF or DOCX file");
        return;
      }
      setResumeFile(file);
      setResultError(null);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClientKey) {
      setResultError("Please select a client");
      return;
    }
    if (!selectedJobId) {
      setResultError("Please select a job");
      return;
    }
    if (!candidateName.trim()) {
      setResultError("Please enter candidate name");
      return;
    }
    if (!resumeFile) {
      setResultError("Please upload a resume");
      return;
    }
    
    setSubmitting(true);
    setResult(null);
    setResultError(null);
    
    try {
      const fileBase64 = await fileToBase64(resumeFile);
      
      const response = await fetch(`${API_BASE}/profiles/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_key: selectedClientKey,
          job_id: parseInt(selectedJobId),
          candidate_name: candidateName.trim(),
          candidate_email: candidateEmail.trim() || undefined,
          file_name: resumeFile.name,
          file_base64: fileBase64
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to create candidate");
      }
      
      const data = await response.json();
      setResult(data);
      setBlueprintReady(false);
      setEmailSent(false);
      
      trackCandidateInvited(data.interview_id, selectedJobId, selectedClientKey);
      
      // Clear form
      setCandidateName("");
      setCandidateEmail("");
      setResumeFile(null);
      const fileInput = document.getElementById("resume-file");
      if (fileInput) fileInput.value = "";
      
    } catch (err) {
      setResultError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const selectedClient = clients.find(c => c.key === selectedClientKey);
  const selectedJob = jobs.find(j => j.id.toString() === selectedJobId);
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Candidate Upload</span>
        </div>
        <button onClick={() => navigate("/admin")} style={styles.backButton}>
          ← Admin Home
        </button>
      </div>
      
      {/* Main Content */}
      <div style={styles.content}>
        <div style={styles.twoColumn}>
          {/* Form Section */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Upload Candidate</h2>
            <p style={styles.sectionText}>
              Select a client and job, then upload a resume to create an interview invitation.
            </p>
            
            <form onSubmit={handleSubmit}>
              {/* Client Selection */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Client</label>
                {loadingClients ? (
                  <p style={styles.loadingText}>Loading clients...</p>
                ) : clients.length === 0 ? (
                  <p style={styles.errorText}>No clients found. Create a client first.</p>
                ) : (
                  <select
                    value={selectedClientKey}
                    onChange={(e) => {
                      setSelectedClientKey(e.target.value);
                      setSelectedJobId("");
                    }}
                    style={styles.select}
                    disabled={submitting}
                  >
                    <option value="">-- Select Client --</option>
                    {clients.map(client => (
                      <option key={client.key} value={client.key}>
                        {client.name || client.key}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Job Selection */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Job</label>
                {!selectedClientKey ? (
                  <p style={styles.hintText}>Select a client first</p>
                ) : loadingJobs ? (
                  <p style={styles.loadingText}>Loading jobs...</p>
                ) : jobs.length === 0 ? (
                  <p style={styles.errorText}>No jobs found for this client.</p>
                ) : (
                  <select
                    value={selectedJobId}
                    onChange={(e) => setSelectedJobId(e.target.value)}
                    style={styles.select}
                    disabled={submitting || loadingJobs}
                  >
                    <option value="">-- Select Job --</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.title || `Job #${job.id}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Candidate Name */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Candidate Name</label>
                <input
                  type="text"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  style={styles.input}
                  placeholder="John Smith"
                  disabled={submitting}
                />
              </div>
              
              {/* Candidate Email */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Candidate Email</label>
                <input
                  type="email"
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  style={styles.input}
                  placeholder="john@example.com"
                  disabled={submitting}
                />
              </div>
              
              {/* Resume Upload */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Resume (PDF or DOCX)</label>
                <input
                  id="resume-file"
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  style={styles.fileInput}
                  disabled={submitting}
                />
                {resumeFile && (
                  <span style={styles.fileName}>{resumeFile.name}</span>
                )}
              </div>
              
              {/* Error Message */}
              {resultError && (
                <div style={styles.errorBox}>
                  {resultError}
                </div>
              )}
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || loadingClients || loadingJobs}
                style={{
                  ...styles.submitButton,
                  backgroundColor: submitting ? "#6b7280" : "#a855f7",
                  cursor: submitting ? "not-allowed" : "pointer"
                }}
              >
                {submitting ? "Creating..." : "Create Candidate"}
              </button>
            </form>
          </div>
          
          {/* Result Section */}
          <div style={styles.resultSection}>
            <h2 style={styles.sectionTitle}>Result</h2>
            
            {!result ? (
              <p style={styles.resultPlaceholder}>
                Created candidates will appear here with interview links and status.
              </p>
            ) : (
              <div style={styles.resultCard}>
                <div style={styles.successIcon}>✓</div>
                <h3 style={styles.resultTitle}>
                  {!blueprintReady ? "Preparing Interview..." : "Interview Ready!"}
                </h3>
                
                <div style={styles.resultField}>
                  <span style={styles.resultLabel}>Candidate</span>
                  <span style={styles.resultValue}>{result.candidate_name}</span>
                </div>
                
                <div style={styles.resultField}>
                  <span style={styles.resultLabel}>Client</span>
                  <span style={styles.resultValue}>{selectedClient?.name || selectedClientKey}</span>
                </div>
                
                <div style={styles.resultField}>
                  <span style={styles.resultLabel}>Job</span>
                  <span style={styles.resultValue}>{selectedJob?.title || `Job #${selectedJobId}`}</span>
                </div>
                
                <div style={styles.resultField}>
                  <span style={styles.resultLabel}>Blueprint Status</span>
                  <span style={{
                    ...styles.resultValue,
                    color: blueprintReady ? "#22c55e" : "#fbbf24"
                  }}>
                    {blueprintReady ? "✓ Ready" : "⏳ Generating..."}
                  </span>
                </div>
                
                {candidateEmail && (
                  <div style={styles.resultField}>
                    <span style={styles.resultLabel}>Email Status</span>
                    <span style={{
                      ...styles.resultValue,
                      color: emailSent ? "#22c55e" : (blueprintReady ? "#fbbf24" : "#6b7280")
                    }}>
                      {emailSent ? "✓ Sent" : (blueprintReady ? "⏳ Sending..." : "⏸ Waiting for blueprint")}
                    </span>
                  </div>
                )}
                
                <div style={styles.urlSection}>
                  <span style={styles.resultLabel}>Interview URL</span>
                  <input
                    type="text"
                    value={result.url}
                    readOnly
                    style={styles.urlInput}
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(result.url)}
                    style={styles.copyButton}
                  >
                    Copy Link
                  </button>
                </div>
                
                {/* Debug Link */}
                {result.interview_id && (
                  <div style={styles.debugSection}>
                    <a
                      href={`/debug/blueprint?interview_id=${result.interview_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.debugLink}
                    >
                      Debug Blueprint →
                    </a>
                  </div>
                )}
                
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    ...styles.openButton, 
                    backgroundColor: blueprintReady ? "#a855f7" : "#6b7280",
                    pointerEvents: blueprintReady ? "auto" : "none"
                  }}
                >
                  {blueprintReady ? "Open Interview →" : "Waiting for Blueprint..."}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  logoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  logoImage: {
    height: 48,
    width: "auto",
    objectFit: "contain"
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer"
  },
  content: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "40px 20px"
  },
  twoColumn: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 40
  },
  formSection: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 24
  },
  resultSection: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 24
  },
  sectionTitle: {
    fontSize: 20,
    marginBottom: 8,
    marginTop: 0,
    color: "#fff"
  },
  sectionText: {
    color: "#9ca3af",
    fontSize: 14,
    marginBottom: 24
  },
  formGroup: {
    marginBottom: 20
  },
  label: {
    display: "block",
    fontSize: 14,
    color: "#9ca3af",
    marginBottom: 8
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "12px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    boxSizing: "border-box"
  },
  fileInput: {
    width: "100%",
    padding: "12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    boxSizing: "border-box"
  },
  fileName: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
    display: "block"
  },
  loadingText: {
    color: "#9ca3af",
    fontSize: 14,
    margin: 0
  },
  hintText: {
    color: "#6b7280",
    fontSize: 14,
    margin: 0
  },
  errorText: {
    color: "#ef4444",
    fontSize: 14,
    margin: 0
  },
  errorBox: {
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 6,
    padding: "12px 16px",
    color: "#ef4444",
    fontSize: 14,
    marginBottom: 20
  },
  submitButton: {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 6,
    color: "#fff",
    fontSize: 16,
    fontWeight: 600
  },
  resultPlaceholder: {
    color: "#6b7280",
    fontSize: 14,
    textAlign: "center",
    padding: "40px 20px"
  },
  resultCard: {
    textAlign: "center"
  },
  successIcon: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(168, 85, 247, 0.1)",
    color: "#a855f7",
    fontSize: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px"
  },
  resultTitle: {
    fontSize: 18,
    marginBottom: 24,
    color: "#fff"
  },
  resultField: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    textAlign: "left"
  },
  resultLabel: {
    fontSize: 14,
    color: "#6b7280"
  },
  resultValue: {
    fontSize: 14,
    color: "#fff",
    fontWeight: 500
  },
  urlSection: {
    marginTop: 20,
    textAlign: "left"
  },
  urlInput: {
    width: "100%",
    padding: "12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    fontSize: 12,
    marginTop: 8,
    boxSizing: "border-box"
  },
  copyButton: {
    marginTop: 8,
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    borderRadius: 6,
    color: "#fff",
    fontSize: 12,
    cursor: "pointer"
  },
  debugSection: {
    marginTop: 16,
    textAlign: "left"
  },
  debugLink: {
    fontSize: 13,
    color: "#6b7280",
    textDecoration: "none"
  },
  openButton: {
    display: "block",
    width: "100%",
    padding: "14px 20px",
    borderRadius: 6,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    textAlign: "center",
    marginTop: 16
  }
};
